import {
  HHAlert,
  HHBox,
  HHButton,
  HHGrid,
  HHStack,
} from '@hinge-health/react-component-library';
import { Skeleton } from '@mui/material';
import { useParams } from 'react-router-dom';
import { BackLink } from '../components/back-link';
import { CursorPagination } from '../components/cursor-pagination';
import { IngestionsTable } from '../components/ingestions-table';
import { FadeIn } from '../components/motion';
import { ProviderConfigurationsTable } from '../components/provider-configurations-table';
import { ProviderHeader } from '../components/provider-header';
import { ProviderKpis } from '../components/provider-kpis';
import { SectionTitle } from '../components/stat-row';
import { SurfaceCard } from '../components/surface-card';
import { WeeklyOutcomesChart } from '../components/weekly-outcomes-chart';
import { useBasePath } from '../context/base-path-context';
import { usePermissions } from '../context/permissions-context';
import { useIngestions } from '../hooks/use-ingestions';
import { useProviderDetail } from '../hooks/use-provider-detail';
import { bucketWeeklyOutcomes } from '../utils/weekly-outcomes.utils';

export const PROVIDER_DETAIL_PAGE_TEST_ID =
  'partner-data-gateway-provider-detail-page';
export const PROVIDER_DETAIL_NO_ACCESS_TEST_ID =
  'partner-data-gateway-provider-detail-no-access';
export const PROVIDER_DETAIL_BACK_LINK_TEST_ID =
  'partner-data-gateway-provider-detail-back-link';
export const PROVIDER_DETAIL_SKELETON_TEST_ID =
  'partner-data-gateway-provider-detail-skeleton';
export const PROVIDER_DETAIL_ERROR_TEST_ID =
  'partner-data-gateway-provider-detail-error';
export const PROVIDER_DETAIL_RETRY_TEST_ID =
  'partner-data-gateway-provider-detail-retry-button';

export const PROVIDER_DETAIL_BACK_LINK_LABEL = 'Back to overview';
export const PROVIDER_DETAIL_NO_ACCESS_MESSAGE =
  'You do not have access to view partner data ingestions.';
export const PROVIDER_DETAIL_ERROR_MESSAGE =
  'Something went wrong loading this provider.';
export const PROVIDER_DETAIL_INGESTIONS_TITLE = 'Ingestions';
export const PROVIDER_DETAIL_CONFIGURATIONS_TITLE = 'Configurations';

const HERO_SKELETON_HEIGHT = 96;
const PANEL_SKELETON_HEIGHT = 240;

const HeroSkeleton = (): JSX.Element => (
  <HHStack gap={4} data-testid={PROVIDER_DETAIL_SKELETON_TEST_ID}>
    <Skeleton
      variant="rectangular"
      height={HERO_SKELETON_HEIGHT}
      sx={{ borderRadius: 1 }}
    />
    <HHGrid container spacing={4}>
      <HHGrid item xs={12} md={8}>
        <Skeleton
          variant="rectangular"
          height={PANEL_SKELETON_HEIGHT}
          sx={{ borderRadius: 1 }}
        />
      </HHGrid>
      <HHGrid item xs={12} md={4}>
        <Skeleton
          variant="rectangular"
          height={PANEL_SKELETON_HEIGHT}
          sx={{ borderRadius: 1 }}
        />
      </HHGrid>
    </HHGrid>
  </HHStack>
);

interface HeroErrorProps {
  onRetry: () => void;
}

/** Same shape as the overview's whole-call error: one alert, one Retry. */
const HeroError = ({ onRetry }: HeroErrorProps): JSX.Element => (
  <HHBox data-testid={PROVIDER_DETAIL_ERROR_TEST_ID}>
    <HHAlert
      hhVariant="outlined"
      severity="error"
      action={
        <HHButton
          hhVariant="variant-bypass"
          variant="text"
          size="small"
          onClick={onRetry}
          data-testid={PROVIDER_DETAIL_RETRY_TEST_ID}
        >
          Retry
        </HHButton>
      }
    >
      {PROVIDER_DETAIL_ERROR_MESSAGE}
    </HHAlert>
  </HHBox>
);

interface ProviderDetailContentProps {
  dataProvider: string;
}

/**
 * The page body, split from the permission gate so no fetch starts for an
 * operator who cannot view ingestions. Nothing here polls: the sample is a
 * snapshot and the ingestions table is a filtered view an operator
 * refreshes on purpose.
 */
const ProviderDetailContent = ({
  dataProvider,
}: ProviderDetailContentProps): JSX.Element => {
  const basePath = useBasePath();
  const detail = useProviderDetail(dataProvider);
  const ingestions = useIngestions({ dataProvider, pollIntervalMs: 0 });

  return (
    <HHStack
      flex={1}
      direction="column"
      gap={4}
      paddingX={6}
      paddingY={6}
      data-testid={PROVIDER_DETAIL_PAGE_TEST_ID}
    >
      <BackLink
        to={basePath || '/'}
        label={PROVIDER_DETAIL_BACK_LINK_LABEL}
        testId={PROVIDER_DETAIL_BACK_LINK_TEST_ID}
      />

      {detail.hasError && <HeroError onRetry={detail.refetch} />}
      {detail.isLoading && !detail.hasError && <HeroSkeleton />}
      {detail.summary && (
        <FadeIn>
          <HHStack gap={4}>
            <SurfaceCard paddingX={5} paddingY={4}>
              <ProviderHeader summary={detail.summary} />
            </SurfaceCard>
            <HHGrid container spacing={4}>
              <HHGrid item xs={12} md={8}>
                <WeeklyOutcomesChart
                  buckets={bucketWeeklyOutcomes(detail.sample)}
                  sampleCount={detail.sample.length}
                />
              </HHGrid>
              <HHGrid item xs={12} md={4}>
                <ProviderKpis
                  summary={detail.summary}
                  configurationsCount={detail.configurations.length}
                />
              </HHGrid>
            </HHGrid>
          </HHStack>
        </FadeIn>
      )}

      <HHBox>
        <HHStack gap={2}>
          <SectionTitle>{PROVIDER_DETAIL_INGESTIONS_TITLE}</SectionTitle>
          <IngestionsTable
            ingestions={ingestions.ingestions}
            isLoading={ingestions.isLoading}
            hasError={ingestions.hasError}
            footer={
              <CursorPagination
                currentPage={ingestions.currentPage}
                hasNext={ingestions.hasNext}
                hasPrevious={ingestions.hasPrevious}
                onNext={ingestions.goToNext}
                onPrevious={ingestions.goToPrevious}
              />
            }
          />
        </HHStack>
      </HHBox>

      <HHBox>
        <HHStack gap={2}>
          <SectionTitle>{PROVIDER_DETAIL_CONFIGURATIONS_TITLE}</SectionTitle>
          <ProviderConfigurationsTable
            configurations={detail.configurations}
            isLoading={detail.configurationsIsLoading}
            hasError={detail.configurationsHasError}
          />
        </HHStack>
      </HHBox>
    </HHStack>
  );
};

/**
 * Permission gate. React Router decodes the path param, so `dataProvider`
 * is the bucket name as PDGS knows it even though the links percent-encode
 * it.
 */
export const ProviderDetailPage = (): JSX.Element => {
  const { dataProvider = '' } = useParams<{ dataProvider: string }>();
  const { canViewIngestions } = usePermissions();

  if (!canViewIngestions) {
    return (
      <HHAlert hhVariant="outlined" severity="warning">
        <span data-testid={PROVIDER_DETAIL_NO_ACCESS_TEST_ID}>
          {PROVIDER_DETAIL_NO_ACCESS_MESSAGE}
        </span>
      </HHAlert>
    );
  }

  return <ProviderDetailContent dataProvider={dataProvider} />;
};

export default ProviderDetailPage;
